import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ref, get } from 'firebase/database';
import { realtimeDb } from '../../firebase';
import {
  FaHome,
  FaFileAlt,
  FaSignOutAlt,
  FaArrowLeft,
  FaFileInvoice,
  FaFileSignature,
} from 'react-icons/fa';
import {
  HiOutlineDocumentAdd,
  HiOutlineDocumentText,
} from 'react-icons/hi';
import { firebaseCompanyKey } from '../../Utils/companyUtils'; // ✅ make sure path is correct

const UserDivisionView = () => {
  const { division } = useParams();
  const navigate = useNavigate();

  const [company, setCompany] = useState('');
  const [info, setInfo] = useState({ address: '', gst: '', tender: '' });
  const [loadingInfo, setLoadingInfo] = useState(true);

  useEffect(() => {
    const userCompany = localStorage.getItem('selectedCompany') || localStorage.getItem('userCompany');
    if (!userCompany) {
      alert('Company not found. Please login again.');
      return;
    }
    setCompany(userCompany.trim());
  }, []);

  useEffect(() => {
    const fetchInfo = async () => {
      if (!company || !division) return;

      const companyKey = firebaseCompanyKey(company); // ✅ Normalize for Firebase
      const path = `companies/${companyKey}/divisions/${division}/info`;

      const infoRef = ref(realtimeDb, path);
      const snapshot = await get(infoRef);
      if (snapshot.exists()) {
        setInfo(snapshot.val());
      }
      setLoadingInfo(false);
    };

    fetchInfo();
  }, [company, division]);

  const handleNavigate = (path) => {
    navigate(path);
  };

  const handleBack = () => {
    navigate('/user/dashboard');
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleBack}
                className="p-2 rounded-full hover:bg-gray-200 transition-colors"
              >
                <FaArrowLeft className="text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                  {division} Division
                </h1>
                <p className="text-gray-600">
                  Company:{' '}
                  <span className="font-bold text-indigo-600 bg-indigo-50 px-2 py-1 rounded-md">
                    {company}
                  </span>
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={handleBack}
            className="flex items-center gap-2 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg transition-colors"
          >
            <FaSignOutAlt /> Back to Dashboard
          </button>
        </div>

        {/* Navigation Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6 mb-8">
          <button
            onClick={() => handleNavigate(`/user/division/${division}/entry`)}
            className="bg-white border border-blue-100 rounded-xl p-6 shadow-sm hover:shadow-md hover:border-blue-200 transition-all text-left"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-blue-100 text-blue-600">
                <HiOutlineDocumentAdd size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-800">Entry Pass</h3>
                <p className="text-sm text-gray-500">Create new entry passes</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => handleNavigate(`/user/division/${division}/register`)}
            className="bg-white border border-green-100 rounded-xl p-6 shadow-sm hover:shadow-md hover:border-green-200 transition-all text-left"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-100 text-green-600">
                <HiOutlineDocumentText size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-800">Register</h3>
                <p className="text-sm text-gray-500">View all entries</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => handleNavigate(`/user/division/${division}/gp-entry`)}
            className="bg-white border border-pink-100 rounded-xl p-6 shadow-sm hover:shadow-md hover:border-pink-200 transition-all text-left"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-pink-100 text-pink-600">
                <FaFileSignature size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-800">GP Entry Pass</h3>
                <p className="text-sm text-gray-500">Create gate passes</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => handleNavigate(`/user/division/${division}/gp-register`)}
            className="bg-white border border-indigo-100 rounded-xl p-6 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all text-left"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-indigo-100 text-indigo-600">
                <FaFileInvoice size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-800">GP Register</h3>
                <p className="text-sm text-gray-500">View all gate passes</p>
              </div>
            </div>
          </button>
        </div>

        {/* Division Info Card */}
        {!loadingInfo && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-full bg-blue-100 text-blue-600">
                <FaHome size={18} />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">
                <span className="text-indigo-600">{company}</span> Division Information
              </h3>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-gray-500 mb-1">Address</h4>
                <p className="text-gray-800">{info.address || 'Not specified'}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-gray-500 mb-1">GST Number</h4>
                <p className="text-gray-800">{info.gst || 'Not specified'}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-gray-500 mb-1">Tender Number</h4>
                <p className="text-gray-800">{info.tender || 'Not specified'}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDivisionView;
